/**
 * mediaProcessor.ts
 * 
 * Media processing utilities for biometric capture
 * Canvas operations, face template extraction, image capture
 * 
 * @author TotalAccess Development Team
 * @date November 3, 2025
 */

import type { FaceBounds } from '../types/attendance.types';
import type { FaceDetectionResult } from '@/hooks/useFacialRecognition';
import { debugLog } from '@/lib/debugLogger';

// ==================== CANVAS OPERATIONS ====================

/**
 * Draw face detection overlay on canvas
 */
export function drawFaceOverlay(
  canvas: HTMLCanvasElement,
  detection: FaceDetectionResult,
  showLandmarks: boolean = true
): void {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx || !detection.detected || !detection.bounds) return;

  const bounds = detection.bounds;
  const width = canvas.width;
  const height = canvas.height;

  // Convert normalized coordinates to canvas coordinates
  const x = bounds.x * width;
  const y = bounds.y * height;
  const w = bounds.width * width;
  const h = bounds.height * height;

  // Draw bounding box
  ctx.strokeStyle = detection.readyToCapture ? '#00ff00' : '#ffaa00';
  ctx.lineWidth = 3;
  ctx.strokeRect(x, y, w, h);

  // Draw face oval guide
  ctx.save();
  ctx.beginPath();
  ctx.ellipse(
    bounds.centerX * width,
    bounds.centerY * height,
    w / 2,
    h / 1.8,
    0,
    0,
    2 * Math.PI
  );
  ctx.strokeStyle = detection.readyToCapture ? '#00ff00' : '#ffaa00';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();

  // Draw landmarks if requested
  if (showLandmarks && detection.landmarks && detection.landmarks.length > 0) {
    ctx.fillStyle = '#00ff00';
    detection.landmarks.forEach((landmark: any) => {
      const lx = landmark.x * width;
      const ly = landmark.y * height;
      ctx.beginPath();
      ctx.arc(lx, ly, 2, 0, 2 * Math.PI);
      ctx.fill();
    });
  }

  // Draw center crosshair
  const centerX = bounds.centerX * width;
  const centerY = bounds.centerY * height;
  ctx.strokeStyle = detection.readyToCapture ? '#00ff00' : '#ffaa00';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(centerX - 10, centerY);
  ctx.lineTo(centerX + 10, centerY);
  ctx.moveTo(centerX, centerY - 10);
  ctx.lineTo(centerX, centerY + 10);
  ctx.stroke();
}

/**
 * Clear canvas overlay
 */
export function clearCanvas(canvas: HTMLCanvasElement): void {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
}

// ==================== IMAGE CAPTURE ====================

/**
 * Capture current frame from video as base64 image
 */
export function captureVideoFrame(
  video: HTMLVideoElement,
  canvas?: HTMLCanvasElement
): string {
  // Create temporary canvas if not provided
  const tempCanvas = canvas || document.createElement('canvas');
  tempCanvas.width = video.videoWidth;
  tempCanvas.height = video.videoHeight;

  const ctx = tempCanvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // Draw current video frame
  ctx.drawImage(video, 0, 0, tempCanvas.width, tempCanvas.height);

  // Convert to base64
  return tempCanvas.toDataURL('image/jpeg', 0.9);
}

/**
 * Capture face region from video
 */
export function captureFaceRegion(
  video: HTMLVideoElement,
  bounds: FaceBounds,
  padding: number = 0.2
): string {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // Add padding to bounds
  const paddedBounds = {
    x: Math.max(0, bounds.x - padding * bounds.width),
    y: Math.max(0, bounds.y - padding * bounds.height),
    width: bounds.width * (1 + 2 * padding),
    height: bounds.height * (1 + 2 * padding)
  };

  // Ensure bounds are within frame
  paddedBounds.width = Math.min(paddedBounds.width, 1 - paddedBounds.x);
  paddedBounds.height = Math.min(paddedBounds.height, 1 - paddedBounds.y);

  // Set canvas size to face region
  const faceWidth = paddedBounds.width * video.videoWidth;
  const faceHeight = paddedBounds.height * video.videoHeight;
  canvas.width = faceWidth;
  canvas.height = faceHeight;

  // Draw face region
  ctx.drawImage(
    video,
    paddedBounds.x * video.videoWidth,
    paddedBounds.y * video.videoHeight,
    faceWidth,
    faceHeight,
    0,
    0,
    faceWidth,
    faceHeight
  );

  return canvas.toDataURL('image/jpeg', 0.9);
}

// ==================== TEMPLATE EXTRACTION ====================

/**
 * Extract face template using face-api.js descriptors ONLY (STRICT MODE)
 * 
 * **CRITICAL**: This function ONLY uses face-api.js and will throw if models aren't loaded.
 * This ensures consistent 128-D descriptors across enrollment and verification.
 * 
 * Use this for ALL enrollment and verification to guarantee template compatibility.
 */

// Cache for face-api module to avoid repeated dynamic imports
let faceApiModule: typeof import('@vladmandic/face-api') | null = null;
let modelsVerified = false;
const FACE_DESCRIPTOR_TIMEOUT_MS = 12000;
// Keep descriptor inputs on the last known-good mobile path: downscale oversized
// frames for face-api, but do not upscale or aggressively re-pad them.
const FACE_DESCRIPTOR_MAX_DIMENSION = 480;
const FACE_DESCRIPTOR_MIN_DIMENSION = 0;

interface FaceDescriptorAttemptInput {
  element: HTMLVideoElement | HTMLCanvasElement;
  label: string;
  minConfidence: number;
  width: number;
  height: number;
}

function createFaceExtractionTimeoutError(timeoutMs: number): Error {
  const error = new Error(
    `Face template extraction timed out after ${Math.ceil(timeoutMs / 1000)} seconds. Please improve lighting and try again.`
  );
  error.name = 'FaceExtractionTimeoutError';
  return error;
}

async function raceWithTimeout<T>(promise: Promise<T>, timeoutMs: number, timeoutErrorFactory: () => Error): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(timeoutErrorFactory()), timeoutMs);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
  }
}

/**
 * Pre-load face-api module for faster descriptor extraction
 * Call this during pre-warming to eliminate dynamic import delay
 */
export async function preloadFaceApiModule(): Promise<boolean> {
  try {
    if (!faceApiModule) {
      debugLog('[MediaProcessor]', '📦 Pre-loading face-api module...');
      faceApiModule = await import('@vladmandic/face-api');
      debugLog('[MediaProcessor]', '✅ Face-api module pre-loaded');
    }
    return true;
  } catch (e) {
    console.error('[MediaProcessor] Failed to pre-load face-api:', e);
    return false;
  }
}

function getDescriptorSourceMetadata(source: HTMLVideoElement | HTMLCanvasElement): {
  width: number;
  height: number;
  sourceType: 'video' | 'canvas';
} {
  if (source instanceof HTMLCanvasElement) {
    return {
      width: source.width,
      height: source.height,
      sourceType: 'canvas',
    };
  }

  return {
    width: source.videoWidth,
    height: source.videoHeight,
    sourceType: 'video',
  };
}

function createCanvasFromSource(
  source: HTMLVideoElement | HTMLCanvasElement,
  width: number,
  height: number,
): HTMLCanvasElement | null {
  if (width <= 0 || height <= 0) {
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    return null;
  }

  ctx.drawImage(source, 0, 0, width, height);
  return canvas;
}

function createSquarePaddedCanvas(source: HTMLVideoElement | HTMLCanvasElement): HTMLCanvasElement | null {
  const metadata = getDescriptorSourceMetadata(source);
  if (metadata.width <= 0 || metadata.height <= 0) {
    return null;
  }

  const longestSide = Math.max(metadata.width, metadata.height);
  const paddedSide = Math.max(longestSide, Math.round(longestSide * 1.14));
  const canvas = document.createElement('canvas');
  canvas.width = paddedSide;
  canvas.height = paddedSide;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    return null;
  }

  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, paddedSide, paddedSide);

  const dx = Math.round((paddedSide - metadata.width) / 2);
  const dy = Math.round((paddedSide - metadata.height) / 2);
  ctx.drawImage(source, dx, dy, metadata.width, metadata.height);

  return canvas;
}

function createPreparedFaceDescriptorCanvas(
  source: HTMLVideoElement | HTMLCanvasElement,
  options?: {
    squarePad?: boolean;
    maxDimension?: number;
    minDimension?: number;
  },
): HTMLCanvasElement | null {
  const maxDimension = options?.maxDimension ?? FACE_DESCRIPTOR_MAX_DIMENSION;
  const minDimension = options?.minDimension ?? FACE_DESCRIPTOR_MIN_DIMENSION;
  const baseSource = options?.squarePad ? createSquarePaddedCanvas(source) : source;

  if (!baseSource) {
    return null;
  }

  const metadata = getDescriptorSourceMetadata(baseSource);
  if (metadata.width <= 0 || metadata.height <= 0) {
    return null;
  }

  let scale = 1;
  const longestSide = Math.max(metadata.width, metadata.height);
  const shortestSide = Math.min(metadata.width, metadata.height);

  if (longestSide > maxDimension) {
    scale = Math.min(scale, maxDimension / longestSide);
  }

  if (shortestSide < minDimension) {
    scale = Math.max(scale, minDimension / shortestSide);
  }

  if (Math.abs(scale - 1) < 0.01) {
    return baseSource instanceof HTMLCanvasElement
      ? baseSource
      : createCanvasFromSource(baseSource, metadata.width, metadata.height);
  }

  const width = Math.max(1, Math.round(metadata.width * scale));
  const height = Math.max(1, Math.round(metadata.height * scale));

  return createCanvasFromSource(baseSource, width, height);
}

function createExposureBoostedCanvas(
  source: HTMLVideoElement | HTMLCanvasElement,
): HTMLCanvasElement | null {
  const baseCanvas = createPreparedFaceDescriptorCanvas(source, {
    maxDimension: FACE_DESCRIPTOR_MAX_DIMENSION,
    minDimension: FACE_DESCRIPTOR_MIN_DIMENSION,
  });

  if (!baseCanvas) {
    return null;
  }

  const baseCtx = baseCanvas.getContext('2d', { willReadFrequently: true });
  if (!baseCtx) {
    return null;
  }

  const imageData = baseCtx.getImageData(0, 0, baseCanvas.width, baseCanvas.height);
  const { data } = imageData;

  let luminanceSum = 0;
  for (let i = 0; i < data.length; i += 4) {
    luminanceSum += (0.2126 * data[i]) + (0.7152 * data[i + 1]) + (0.0722 * data[i + 2]);
  }

  const averageLuminance = luminanceSum / Math.max(1, data.length / 4);
  if (averageLuminance >= 142) {
    return null;
  }

  const brightnessGain = Math.min(1.28, Math.max(1.08, 142 / Math.max(averageLuminance, 1)));
  const contrastGain = averageLuminance < 118 ? 1.12 : 1.06;
  const gamma = averageLuminance < 108 ? 0.88 : 0.94;

  const boostedCanvas = document.createElement('canvas');
  boostedCanvas.width = baseCanvas.width;
  boostedCanvas.height = baseCanvas.height;
  const boostedCtx = boostedCanvas.getContext('2d', { willReadFrequently: true });
  if (!boostedCtx) {
    return null;
  }

  const boostedImageData = boostedCtx.createImageData(baseCanvas.width, baseCanvas.height);
  const boostedData = boostedImageData.data;

  for (let i = 0; i < data.length; i += 4) {
    for (let channel = 0; channel < 3; channel += 1) {
      const normalized = data[i + channel] / 255;
      const gammaAdjusted = Math.pow(normalized, gamma);
      const contrastAdjusted = ((gammaAdjusted * 255) - 128) * contrastGain + 128;
      boostedData[i + channel] = Math.max(0, Math.min(255, Math.round(contrastAdjusted * brightnessGain)));
    }

    boostedData[i + 3] = data[i + 3];
  }

  boostedCtx.putImageData(boostedImageData, 0, 0);
  return boostedCanvas;
}

function buildFaceDescriptorAttemptInputs(
  source: HTMLVideoElement | HTMLCanvasElement,
): FaceDescriptorAttemptInput[] {
  const sourceMetadata = getDescriptorSourceMetadata(source);
  const attempts: FaceDescriptorAttemptInput[] = [];

  const pushAttempt = (
    element: HTMLVideoElement | HTMLCanvasElement | null,
    label: string,
    minConfidence: number,
  ) => {
    if (!element) {
      return;
    }

    const metadata = getDescriptorSourceMetadata(element);
    const duplicate = attempts.some(
      (attempt) => attempt.width === metadata.width
        && attempt.height === metadata.height
        && attempt.minConfidence === minConfidence
    );

    if (duplicate) {
      return;
    }

    attempts.push({
      element,
      label,
      minConfidence,
      width: metadata.width,
      height: metadata.height,
    });
  };

  // Primary attempts with standard confidence thresholds
  pushAttempt(
    createPreparedFaceDescriptorCanvas(source, {
      maxDimension: FACE_DESCRIPTOR_MAX_DIMENSION,
      minDimension: FACE_DESCRIPTOR_MIN_DIMENSION,
    }),
    'prepared',
    0.4,
  );

  pushAttempt(
    createExposureBoostedCanvas(source),
    'shadow_boosted',
    0.28,
  );

  const shouldTryOriginal = sourceMetadata.width > FACE_DESCRIPTOR_MAX_DIMENSION
    || sourceMetadata.height > FACE_DESCRIPTOR_MAX_DIMENSION
    || source instanceof HTMLCanvasElement;

  if (shouldTryOriginal) {
    pushAttempt(source, 'original', 0.35);
  }

  // Fallback attempts with more aggressive thresholds for difficult cases
  pushAttempt(
    createPreparedFaceDescriptorCanvas(source, {
      maxDimension: FACE_DESCRIPTOR_MAX_DIMENSION,
      minDimension: FACE_DESCRIPTOR_MIN_DIMENSION,
      squarePad: true,
    }),
    'prepared_padded',
    0.25,
  );

  pushAttempt(
    createPreparedFaceDescriptorCanvas(source, {
      maxDimension: FACE_DESCRIPTOR_MAX_DIMENSION * 1.2, // Allow slightly larger
      minDimension: FACE_DESCRIPTOR_MIN_DIMENSION,
    }),
    'prepared_larger',
    0.22,
  );

  // Very low confidence fallback for edge cases
  pushAttempt(
    createPreparedFaceDescriptorCanvas(source, {
      maxDimension: FACE_DESCRIPTOR_MAX_DIMENSION,
      minDimension: FACE_DESCRIPTOR_MIN_DIMENSION,
    }),
    'prepared_aggressive',
    0.15,
  );

  return attempts;
}

export async function extractFaceApiDescriptor(
  sourceElement: HTMLVideoElement | HTMLCanvasElement,
  faceBounds?: { x: number; y: number; width: number; height: number } | null,
): Promise<Float32Array | null> {
  const startTime = performance.now();
  debugLog('[MediaProcessor]', '🔐 Extracting face-api.js descriptor (STRICT MODE)...', {
    hasProvidedBounds: !!faceBounds,
  });

  try {
    return await raceWithTimeout(
      (async () => {
        // Use cached module or import if needed
        const faceapi = faceApiModule || await import('@vladmandic/face-api');
        if (!faceApiModule) {
          faceApiModule = faceapi;
        }

        const importTime = performance.now() - startTime;
        debugLog('[MediaProcessor]', `Module import: ${importTime.toFixed(0)}ms`);

        // Only verify models once per session
        if (!modelsVerified) {
          if (!faceapi.nets.ssdMobilenetv1.isLoaded) {
            console.error('[MediaProcessor] ❌ ssdMobilenetv1 model not loaded');
            throw new Error('Face recognition models not loaded. Please reload the page.');
          }
          if (!faceapi.nets.faceLandmark68Net.isLoaded) {
            console.error('[MediaProcessor] ❌ faceLandmark68Net model not loaded');
            throw new Error('Face recognition models not loaded. Please reload the page.');
          }
          if (!faceapi.nets.faceRecognitionNet.isLoaded) {
            console.error('[MediaProcessor] ❌ faceRecognitionNet model not loaded');
            throw new Error('Face recognition models not loaded. Please reload the page.');
          }
          modelsVerified = true;
          debugLog('[MediaProcessor]', '✅ All face-api.js models are loaded');
        }

        const modelCheckTime = performance.now() - startTime;
        debugLog('[MediaProcessor]', `Model check: ${modelCheckTime.toFixed(0)}ms`);
        const sourceMetadata = getDescriptorSourceMetadata(sourceElement);

        let result:
          | (Awaited<ReturnType<ReturnType<typeof faceapi.detectSingleFace>['withFaceLandmarks']>> & {
              descriptor: Float32Array;
            })
          | null = null;
        type FaceApiResult = NonNullable<typeof result>;
        let successfulAttempt: FaceDescriptorAttemptInput | null = null;

        // ── Path A: Use MediaPipe-provided bounds to bypass SSD detection ──
        // When we already have face bounds from MediaPipe, construct a FaceDetection
        // manually and run landmark + descriptor nets directly. This avoids the SSD
        // MobileNet v1 detector which fails on certain faces (e.g. low-light, certain
        // angles, glasses) even though MediaPipe already found them.
        if (faceBounds && faceBounds.width > 0 && faceBounds.height > 0) {
          debugLog('[MediaProcessor]', 'Trying descriptor extraction with provided bounds (bypassing SSD)', {
            bounds: `${faceBounds.width}x${faceBounds.height}@${faceBounds.x},${faceBounds.y}`,
            sourceSize: `${sourceMetadata.width}x${sourceMetadata.height}`,
          });

          try {
            const preparedCanvas = createPreparedFaceDescriptorCanvas(sourceElement);
            if (preparedCanvas) {
              const prepMetadata = getDescriptorSourceMetadata(preparedCanvas);
              const scaleX = prepMetadata.width / sourceMetadata.width;
              const scaleY = prepMetadata.height / sourceMetadata.height;

              const scaledBounds = new faceapi.Rect(
                faceBounds.x * scaleX,
                faceBounds.y * scaleY,
                faceBounds.width * scaleX,
                faceBounds.height * scaleY,
              );

              const fakeDetection = new faceapi.FaceDetection(
                1.0,
                scaledBounds,
                { width: prepMetadata.width, height: prepMetadata.height },
              );

              const faceImage = await faceapi.extractFaces(preparedCanvas, [fakeDetection]);
              if (faceImage && faceImage[0]) {
                const landmarks = await faceapi.nets.faceLandmark68Net.detectLandmarks(faceImage[0]) as InstanceType<typeof faceapi.FaceLandmarks68>;
                if (landmarks) {
                  const alignedRect = landmarks.align(fakeDetection, { useDlibAlignment: true });
                  const alignedFaces = await faceapi.extractFaces(preparedCanvas, [alignedRect]);
                  if (alignedFaces && alignedFaces[0]) {
                    const descriptor = await faceapi.nets.faceRecognitionNet.computeFaceDescriptor(alignedFaces[0]) as Float32Array;
                    if (descriptor && descriptor.length === 128) {
                      // Check for constant descriptor (model malfunction) before accepting Path A result.
                      // If the descriptor has near-zero variance, discard it and fall through to Path B
                      // (SSD-based detection) which may produce a valid descriptor via a different code path.
                      const pathA_sum = Array.from(descriptor).reduce((a, b) => a + b, 0);
                      const pathA_mean = pathA_sum / 128;
                      const pathA_variance = Array.from(descriptor).reduce((acc, v) => acc + (v - pathA_mean) * (v - pathA_mean), 0) / 128;
                      const pathA_stdDev = Math.sqrt(pathA_variance);

                      if (pathA_stdDev < 0.01) {
                        console.warn('[MediaProcessor] ⚠️ Path A (provided bounds) produced constant descriptor (stdDev:', pathA_stdDev.toFixed(6), ') — discarding and falling through to SSD-based detection');
                      } else {
                        result = {
                          detection: fakeDetection,
                          landmarks,
                          unshiftedLandmarks: landmarks,
                          alignedRect: new faceapi.FaceDetection(
                            fakeDetection.score,
                            alignedRect,
                            { width: prepMetadata.width, height: prepMetadata.height },
                          ),
                          descriptor,
                          angle: { roll: undefined, pitch: undefined, yaw: undefined },
                        } as FaceApiResult;
                        successfulAttempt = {
                          element: preparedCanvas,
                          label: 'provided_bounds',
                          minConfidence: 1.0,
                          width: prepMetadata.width,
                          height: prepMetadata.height,
                        };
                        debugLog('[MediaProcessor]', '✅ Descriptor extracted using provided bounds (SSD bypassed)');
                      }
                    }
                  }
                }
              }
            }
          } catch (boundsError) {
            debugLog('[MediaProcessor]', 'Provided-bounds extraction failed, falling back to SSD attempts', {
              error: boundsError instanceof Error ? boundsError.message : String(boundsError),
            });
          }
        }

        // ── Path B: Standard SSD-based attempts (existing logic) ──
        const attemptInputs = buildFaceDescriptorAttemptInputs(sourceElement);

        if (!result) {
          const resizeTime = performance.now() - startTime;
          debugLog('[MediaProcessor]', `Resize: ${resizeTime.toFixed(0)}ms`);

          for (const attempt of attemptInputs) {
            debugLog('[MediaProcessor]', 'Trying face descriptor extraction attempt', {
              attempt: attempt.label,
              minConfidence: attempt.minConfidence,
              size: `${attempt.width}x${attempt.height}`,
              sourceType: sourceMetadata.sourceType,
            });

            const attemptResult = await faceapi
              .detectSingleFace(attempt.element, new faceapi.SsdMobilenetv1Options({ minConfidence: attempt.minConfidence }))
              .withFaceLandmarks()
              .withFaceDescriptor();

            if (attemptResult) {
              result = attemptResult;
              successfulAttempt = attempt;
              break;
            }
          }
        }

        const detectionTime = performance.now() - startTime;
        debugLog('[MediaProcessor]', `Detection complete: ${detectionTime.toFixed(0)}ms`);

        if (!result) {
          debugLog('[MediaProcessor]', 'Face scan attempt logged: no face detected in frame.', {
            sourceType: sourceMetadata.sourceType,
            sourceSize: `${sourceMetadata.width}x${sourceMetadata.height}`,
            attempts: attemptInputs.map((attempt) => `${attempt.label}:${attempt.width}x${attempt.height}@${attempt.minConfidence}`),
          });
          return null;
        }

        if (!result.descriptor) {
          console.error('[MediaProcessor] ❌ Face detected but no descriptor generated');
          throw new Error('Failed to generate face descriptor. Please try again.');
        }

        // Validate descriptor format
        if (result.descriptor.length !== 128) {
          console.error('[MediaProcessor] ❌ Invalid descriptor length:', result.descriptor.length);
          throw new Error('Invalid face descriptor format. Please reload the page and try again.');
        }

        // Validate descriptor values are in expected range (-0.5 to +0.5 typically)
        const sum = Array.from(result.descriptor).reduce((a, b) => a + b, 0);
        const absSum = Array.from(result.descriptor).reduce((a, b) => a + Math.abs(b), 0);

        debugLog('[MediaProcessor]', '✅ face-api.js 128-D descriptor extracted:', {
          dimensions: result.descriptor.length,
          detection_score: result.detection.score.toFixed(3),
          extraction_attempt: successfulAttempt?.label || 'unknown',
          extraction_input_size: successfulAttempt ? `${successfulAttempt.width}x${successfulAttempt.height}` : 'unknown',
          first_5_values: Array.from(result.descriptor.slice(0, 5)).map(v => v.toFixed(4)),
          last_5_values: Array.from(result.descriptor.slice(-5)).map(v => v.toFixed(4)),
          sum: sum.toFixed(4),
          absSum: absSum.toFixed(4)
        });

        // Sanity check: face-api.js descriptors typically have absSum between 3 and 10
        if (absSum < 1 || absSum > 20) {
          console.warn('[MediaProcessor] ⚠️ Unusual descriptor values - sum:', sum, 'absSum:', absSum);
        }

        // CRITICAL: Check for constant descriptor output (model malfunction detection)
        // On certain mobile GPUs (e.g. Mali GPUs in Samsung Exynos), the TensorFlow.js
        // WebGL backend can silently produce a constant descriptor regardless of input.
        // This manifests as very low variance across all 128 dimensions — every value
        // is nearly identical. A healthy descriptor has high variance across dimensions.
        const descriptorValues = Array.from(result.descriptor);
        const mean = sum / 128;
        const variance = descriptorValues.reduce((acc, v) => acc + (v - mean) * (v - mean), 0) / 128;
        const stdDev = Math.sqrt(variance);
        if (stdDev < 0.01) {
          console.error('[MediaProcessor] 🚨 CONSTANT DESCRIPTOR DETECTED - stdDev:', stdDev.toFixed(6), 'mean:', mean.toFixed(6), 'extraction_path:', successfulAttempt?.label || 'unknown', 'This indicates a model malfunction (likely WebGL backend issue on this device).');
          throw new Error('Face recognition model is not working correctly on this device. Please restart the app or try a different device.');
        }

        // L2 normalize the descriptor for consistent comparisons
        // This ensures Euclidean distance works correctly across different captures
        const l2Norm = Math.sqrt(Array.from(result.descriptor).reduce((a, b) => a + b * b, 0));
        const normalizedDescriptor = new Float32Array(128);

        if (l2Norm > 0) {
          for (let i = 0; i < 128; i++) {
            normalizedDescriptor[i] = result.descriptor[i] / l2Norm;
          }
          debugLog('[MediaProcessor]', '✅ Descriptor L2 normalized:', {
            original_l2_norm: l2Norm.toFixed(4),
            normalized_l2_norm: Math.sqrt(Array.from(normalizedDescriptor).reduce((a, b) => a + b * b, 0)).toFixed(4)
          });
        } else {
          console.warn('[MediaProcessor] ⚠️ Cannot normalize - L2 norm is zero');
          return result.descriptor;
        }

        return normalizedDescriptor;
      })(),
      FACE_DESCRIPTOR_TIMEOUT_MS,
      () => createFaceExtractionTimeoutError(FACE_DESCRIPTOR_TIMEOUT_MS)
    );
  } catch (error) {
    if (error instanceof Error && error.name === 'FaceExtractionTimeoutError') {
      const sourceMetadata = getDescriptorSourceMetadata(sourceElement);
      console.error('[MediaProcessor] ❌ Face descriptor extraction timed out', {
        timeoutMs: FACE_DESCRIPTOR_TIMEOUT_MS,
        sourceType: sourceMetadata.sourceType,
        sourceSize: `${sourceMetadata.width}x${sourceMetadata.height}`
      });
    }
    throw error;
  }
}

/**
 * Extract face template using face-api.js descriptors (preferred) or MediaPipe landmarks (fallback)
 * 
 * **DEPRECATED**: Use extractFaceApiDescriptor() instead for consistent templates.
 * This function is kept for backward compatibility only.
 * 
 * **IMPORTANT**: MediaPipe landmarks alone are NOT discriminative enough for face recognition!
 * They only capture geometric distances which can be very similar for family members.
 * 
 * face-api.js uses a deep neural network to extract 128 discriminative features that
 * can distinguish between similar faces.
 */
export async function extractFaceTemplate(detection: FaceDetectionResult, videoElement?: HTMLVideoElement): Promise<Float32Array> {
  // If video element provided, prefer the strict face-api.js extraction
  if (videoElement) {
    try {
      const descriptor = await extractFaceApiDescriptor(videoElement);
      if (descriptor) {
        return descriptor;
      }
      debugLog('[MediaProcessor]', 'face-api.js did not detect a face in frame; evaluating MediaPipe fallback...');
    } catch (faceApiError: unknown) {
      console.warn(
        '[MediaProcessor] ⚠️ face-api.js extraction failed:',
        faceApiError instanceof Error ? faceApiError.message : String(faceApiError)
      );
      // Only fall back if explicitly allowed (e.g., offline mode)
      console.warn('[MediaProcessor] ⚠️ Falling back to MediaPipe (NOT RECOMMENDED for enrollment)');
    }
  }

  // Validate detection has required data for MediaPipe fallback
  if (!detection.landmarks || detection.landmarks.length === 0 || !detection.bounds) {
    console.error('[MediaProcessor] ❌ Invalid detection data for template extraction:', {
      hasLandmarks: !!detection.landmarks,
      landmarkCount: detection.landmarks?.length || 0,
      hasBounds: !!detection.bounds,
      detectionConfidence: detection.confidence
    });
    throw new Error('Cannot extract template: No face landmarks detected. Please ensure face is clearly visible.');
  }

  // Require minimum confidence for reliable template
  if (detection.confidence < 0.6) {
    console.error('[MediaProcessor] ❌ Detection confidence too low:', detection.confidence);
    throw new Error(`Face detection confidence too low (${(detection.confidence * 100).toFixed(0)}%). Please improve lighting and face positioning.`);
  }

  // FALLBACK: Use MediaPipe landmarks (less discriminative, but works offline)
  console.warn('[MediaProcessor] ⚠️ Using MediaPipe landmark-based template (FALLBACK - less accurate for similar faces)');
  return extractMediaPipeLandmarkTemplate(detection);
}

/**
 * Extract template from MediaPipe landmarks (FALLBACK ONLY)
 * NOTE: This is NOT discriminative enough for faces with similar geometry!
 */
function extractMediaPipeLandmarkTemplate(detection: FaceDetectionResult): Float32Array {
  const TEMPLATE_DIMENSIONS = 128;

  const template = new Float32Array(TEMPLATE_DIMENSIONS);

  // Get bounding box for normalization
  const bounds = detection.bounds;
  if (!bounds) {
    throw new Error('Cannot extract template: Face bounds unavailable. Please try again.');
  }
  const centerX = bounds.centerX || (bounds.x + bounds.width / 2);
  const centerY = bounds.centerY || (bounds.y + bounds.height / 2);
  const scale = Math.max(bounds.width, bounds.height);

  // Extract key facial landmarks (MediaPipe provides 6 key points)
  // 0: Right Eye, 1: Left Eye, 2: Nose Tip, 3: Mouth Center, 4: Right Ear, 5: Left Ear
  const landmarks = detection.landmarks;

  debugLog('[MediaProcessor]', 'Extracting template from landmarks:', {
    landmarkCount: landmarks.length,
    boundingBox: bounds,
    confidence: detection.confidence
  });

  // Normalize each landmark relative to face center and scale
  for (let i = 0; i < Math.min(landmarks.length, 6); i++) {
    const lm = landmarks[i];
    const baseIdx = i * 20; // Use 20 dimensions per landmark

    if (baseIdx + 19 < TEMPLATE_DIMENSIONS) {
      // Position (normalized x, y, z)
      template[baseIdx + 0] = (lm.x - centerX) / scale;
      template[baseIdx + 1] = (lm.y - centerY) / scale;
      template[baseIdx + 2] = (lm.z || 0) / scale;

      // Distances to other landmarks (geometric relationships)
      for (let j = 0; j < Math.min(landmarks.length, 6); j++) {
        if (i !== j && baseIdx + 3 + j < TEMPLATE_DIMENSIONS) {
          const other = landmarks[j];
          const dx = lm.x - other.x;
          const dy = lm.y - other.y;
          const dz = (lm.z || 0) - (other.z || 0);
          const dist = Math.sqrt(dx * dx + dy * dy + dz * dz) / scale;
          template[baseIdx + 3 + j] = dist;
        }
      }

      // Angular features (angles between landmark and face center)
      const angle = Math.atan2(lm.y - centerY, lm.x - centerX);
      template[baseIdx + 10] = Math.cos(angle);
      template[baseIdx + 11] = Math.sin(angle);

      // Relative positions to eyes (if available)
      if (landmarks.length >= 2) {
        const eyeCenterX = (landmarks[0].x + landmarks[1].x) / 2;
        const eyeCenterY = (landmarks[0].y + landmarks[1].y) / 2;
        template[baseIdx + 12] = (lm.x - eyeCenterX) / scale;
        template[baseIdx + 13] = (lm.y - eyeCenterY) / scale;
      }

      // Face width/height ratios (biometric features)
      template[baseIdx + 14] = bounds.width / scale;
      template[baseIdx + 15] = bounds.height / scale;
      template[baseIdx + 16] = bounds.width / bounds.height; // aspect ratio

      // Confidence and visibility scores
      template[baseIdx + 17] = detection.confidence;
      template[baseIdx + 18] = lm.visibility || 1.0;
      template[baseIdx + 19] = detection.metrics?.quality || 0.8;
    }
  }

  // Fill remaining dimensions with derived features
  if (landmarks.length >= 3) {
    // Inter-eye distance (normalized)
    const eyeDist = Math.sqrt(
      Math.pow(landmarks[1].x - landmarks[0].x, 2) +
      Math.pow(landmarks[1].y - landmarks[0].y, 2)
    ) / scale;
    template[120] = eyeDist;

    // Nose-to-mouth distance
    if (landmarks.length >= 4) {
      const noseMouthDist = Math.sqrt(
        Math.pow(landmarks[3].x - landmarks[2].x, 2) +
        Math.pow(landmarks[3].y - landmarks[2].y, 2)
      ) / scale;
      template[121] = noseMouthDist;
    }

    // Face symmetry (left vs right eye distance from nose)
    const leftEyeNoseDist = Math.sqrt(
      Math.pow(landmarks[2].x - landmarks[1].x, 2) +
      Math.pow(landmarks[2].y - landmarks[1].y, 2)
    ) / scale;
    const rightEyeNoseDist = Math.sqrt(
      Math.pow(landmarks[2].x - landmarks[0].x, 2) +
      Math.pow(landmarks[2].y - landmarks[0].y, 2)
    ) / scale;
    template[122] = leftEyeNoseDist;
    template[123] = rightEyeNoseDist;
    template[124] = leftEyeNoseDist / rightEyeNoseDist; // symmetry ratio
  }

  // Overall face metrics
  template[125] = detection.confidence;
  template[126] = scale; // normalized face size
  template[127] = detection.metrics?.stability || 1.0;

  // Normalize template to unit length (cosine similarity will work better)
  let magnitude = 0;
  for (let i = 0; i < TEMPLATE_DIMENSIONS; i++) {
    magnitude += template[i] * template[i];
  }
  magnitude = Math.sqrt(magnitude);

  if (magnitude > 0) {
    for (let i = 0; i < TEMPLATE_DIMENSIONS; i++) {
      template[i] /= magnitude;
    }
  }

  // Validate template quality
  const nonZeroCount = Array.from(template).filter(v => Math.abs(v) > 0.0001).length;
  const validFeatureRatio = nonZeroCount / TEMPLATE_DIMENSIONS;

  debugLog('[MediaProcessor]', '✅ Extracted MediaPipe landmark embedding:', {
    dimensions: TEMPLATE_DIMENSIONS,
    landmarksUsed: Math.min(landmarks.length, 6),
    sampleValues: Array.from(template.slice(0, 5)),
    nonZeroCount,
    validFeatureRatio: `${(validFeatureRatio * 100).toFixed(1)}%`,
    magnitude: magnitude.toFixed(4)
  });

  // Require at least 40% non-zero features for valid template
  if (validFeatureRatio < 0.4) {
    console.error('[MediaProcessor] ❌ Template quality too low:', {
      validFeatureRatio,
      nonZeroCount,
      required: Math.ceil(TEMPLATE_DIMENSIONS * 0.4)
    });
    throw new Error(`Template quality insufficient (${(validFeatureRatio * 100).toFixed(0)}% features). Please ensure face is well-lit and clearly visible.`);
  }

  return template;
}

/**
 * Generate landmark-based template as fallback
 * Used when face-api.js is not available or fails
 */
function generateLandmarkBasedTemplate(detection: FaceDetectionResult): Float32Array {
  const TEMPLATE_DIMENSIONS = 128;
  const template = new Float32Array(TEMPLATE_DIMENSIONS);

  if (!detection.landmarks || detection.landmarks.length === 0) {
    console.warn('[MediaProcessor] No landmarks available for template extraction');
    // Return random template as last resort
    for (let i = 0; i < TEMPLATE_DIMENSIONS; i++) {
      template[i] = Math.random();
    }
    return template;
  }

  // Generate feature vector from facial landmarks
  // This creates a deterministic but unique template based on facial geometry

  // 1. Landmark positions (normalized coordinates)
  for (let i = 0; i < Math.min(50, detection.landmarks.length); i++) {
    const landmark = detection.landmarks[i];
    if (i * 2 < TEMPLATE_DIMENSIONS) template[i * 2] = landmark.x;
    if (i * 2 + 1 < TEMPLATE_DIMENSIONS) template[i * 2 + 1] = landmark.y;
  }

  // 2. Geometric features (distances, angles)
  if (detection.landmarks.length >= 6) {
    const leftEye = detection.landmarks[0];
    const rightEye = detection.landmarks[1];
    const nose = detection.landmarks[2];
    const mouth = detection.landmarks[3];

    const eyeDistance = Math.sqrt(
      Math.pow(rightEye.x - leftEye.x, 2) +
      Math.pow(rightEye.y - leftEye.y, 2)
    );

    const faceHeight = Math.sqrt(
      Math.pow(nose.x - mouth.x, 2) +
      Math.pow(nose.y - mouth.y, 2)
    );

    // Add geometric ratios
    if (TEMPLATE_DIMENSIONS > 100) {
      template[100] = eyeDistance;
      template[101] = faceHeight;
      template[102] = eyeDistance / (faceHeight + 0.001); // Face proportion
    }
  }

  // 3. Quality metrics as features
  if (TEMPLATE_DIMENSIONS > 110) {
    template[110] = detection.metrics.quality;
    template[111] = detection.metrics.confidence;
    template[112] = detection.distance.distanceScore;
    template[113] = detection.lighting.lightingScore;
  }

  // Normalize the template to [-1, 1] range
  let sum = 0;
  for (let i = 0; i < TEMPLATE_DIMENSIONS; i++) {
    sum += template[i] * template[i];
  }
  const norm = Math.sqrt(sum);
  if (norm > 0) {
    for (let i = 0; i < TEMPLATE_DIMENSIONS; i++) {
      template[i] = template[i] / norm;
    }
  }

  debugLog('[MediaProcessor]', 'Generated landmark-based template (fallback):', {
    dimensions: TEMPLATE_DIMENSIONS,
    landmarkCount: detection.landmarks.length,
    sampleValues: Array.from(template.slice(0, 5))
  });

  return template;
}

/**
 * Prepare template data for backend submission
 */
export function prepareTemplateForSubmission(
  template: Float32Array,
  detection: FaceDetectionResult
): {
  template: number[];
  format: string;
  confidence: number;
  quality: number;
  metadata: Record<string, unknown>;
} {
  return {
    template: Array.from(template),
    format: 'mediapipe_embedding',
    confidence: detection.metrics.confidence,
    quality: detection.metrics.quality,
    metadata: {
      presence: detection.metrics.presence,
      stability: detection.metrics.stability,
      liveness: detection.metrics.liveness,
      distance: detection.metrics.distance,
      lighting: detection.lighting.status,
      brightness: detection.lighting.brightness,
      timestamp: Date.now()
    }
  };
}

// ==================== VIDEO UTILITIES ====================

/**
 * Get optimal video constraints for face detection
 */
export function getVideoConstraints(): MediaStreamConstraints {
  return {
    video: {
      facingMode: 'user',
      width: { ideal: 1280, min: 640 },
      height: { ideal: 720, min: 480 },
      aspectRatio: { ideal: 16 / 9 }
    },
    audio: false
  };
}

/**
 * Stop video stream and release camera
 */
export function stopVideoStream(stream: MediaStream | null): void {
  if (stream) {
    stream.getTracks().forEach(track => {
      track.stop();
      debugLog('[MediaProcessor]', 'Stopped video track:', track.label);
    });
  }
}

// ==================== IMAGE PROCESSING ====================

/**
 * Resize image to maximum dimensions while preserving aspect ratio
 */
export function resizeImage(
  base64Image: string,
  maxWidth: number,
  maxHeight: number
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;

      // Calculate new dimensions
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', 0.9));
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = base64Image;
  });
}

/**
 * Convert base64 to blob
 */
export function base64ToBlob(base64: string, mimeType: string = 'image/jpeg'): Blob {
  const byteString = atob(base64.split(',')[1]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);

  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new Blob([ab], { type: mimeType });
}
